import type { TileState } from './constants'
import type { MenuProduct } from '@/api/endpoints/menu-products'
import type { PosLayout, PosLayoutPage } from '@/api/types'

/**
 * A planned product that is on no page of the layout, with the SKU a placement would use.
 *
 * `placementSku` differs from `sku` only for a merged duplicate: the POS sells the primary, so
 * that is what a tile has to carry (CHG-2026-09-duplicate-product-merge).
 */
export interface MissingMenuProduct extends MenuProduct {
  placementSku: string
}

/** The SKU a tile for this product must carry to be sellable. */
function placementSkuOf(product: MenuProduct): string {
  return product.mergedIntoSku ?? product.sku
}

/**
 * Planned products that no tile covers, in the order the menus give them.
 *
 * Diffed against the layout the editor currently holds — including unsaved placements — because
 * the point of the list is to empty as the user works, not after a save and a reload.
 *
 * A product counts as placed when either its own SKU or its primary's is on a tile: a merged
 * duplicate whose primary already has a tile is a dish the till can ring up, and reporting it as
 * missing would teach people to ignore the list.
 */
export function findMissingFromLayout(
  menuProducts: Array<MenuProduct>,
  layout: PosLayout,
): Array<MissingMenuProduct> {
  const placed = new Set(
    layout.pages.flatMap((page) => page.tiles.map((tile) => tile.sku)),
  )
  const missing: Array<MissingMenuProduct> = []
  const seen = new Set<string>()
  for (const product of menuProducts) {
    const placementSku = placementSkuOf(product)
    if (placed.has(placementSku) || placed.has(product.sku)) continue
    // Two duplicates of the same primary are one missing tile, not two.
    if (seen.has(placementSku)) continue
    seen.add(placementSku)
    missing.push({ ...product, placementSku })
  }
  return missing
}

/**
 * The first free cell of a page, scanned row by row from the top left — `null` when the page is
 * full.
 *
 * Cells are 1-based, as the editor's grid renders them. Growing the grid to make room is
 * deliberately not this function's job: the size of a POS page is a decision about the terminal,
 * not a side effect of a convenience button.
 */
export function findNextFreeCell(
  page: PosLayoutPage,
): { row: number; col: number } | null {
  const occupied = new Set(page.tiles.map((tile) => `${tile.row},${tile.col}`))
  for (let row = 1; row <= page.grid.rows; row += 1) {
    for (let col = 1; col <= page.grid.columns; col += 1) {
      if (!occupied.has(`${row},${col}`)) return { row, col }
    }
  }
  return null
}

/**
 * The tile state a planned product would have if it were placed now.
 *
 * Same vocabulary and same order of precedence as `getTileState`, so a row in the panel warns about
 * exactly what the tile would warn about once it exists. `pending` and `unresolved` cannot occur
 * here: these facts arrive with the row rather than from a lookup that may still be in flight.
 */
export function menuProductState(product: MenuProduct): TileState {
  if (!product.assignedHere) return 'not-assigned'
  if (!product.active) return 'deactivated'
  if (product.openPrice) return 'open-price'
  if (product.priceMissing) return 'no-price'
  return 'ready'
}
